import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MENTION_DND_MIME,
  type FileTreeEntry,
  type TriggerBanner,
  type TriggerConfig,
  type TriggerItem,
  type TriggerSection,
  type MentionRef as UiMentionRef,
} from '@localagent/ui';
import type {
  MentionRef as ChatMentionRef,
  SearchResult,
  WorkspaceRoutingClass,
} from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';

const DEBOUNCE_MS = 200;
const SEARCH_LIMIT = 20;
const EMPTY_BANNERS: TriggerBanner[] = [];

export interface UseFileMentionsOptions {
  knownEntries: FileTreeEntry[];
  workspaceId: string | null;
  mode?: WorkspaceRoutingClass;
  workspacePath?: string | null;
  indexingReady: boolean;
  executeLocalTool?: (
    toolId: string,
    params: Record<string, unknown>
  ) => Promise<{
    error?: string;
    matches?: string[];
    entries?: Array<{ name: string; is_dir?: boolean; size?: number }>;
  }>;
}

/**
 * Path-like queries trigger the glob leg: `/`, `\`, or glob metacharacters.
 * Basename-only queries use search alone.
 */
export function isPathLikeQuery(query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  return /[/\\*?[\]]/.test(q);
}

/**
 * Canonical Indexing/unready empty-catalog UI.
 * Unlabeled browse-only (items without these labels) is a defect — callers must
 * not substitute a flat `items` list for this shape while unready.
 */
export function unreadyBrowseTriggerState(browseItems: TriggerItem[]): {
  banners: TriggerBanner[];
  sections: TriggerSection[];
} {
  return {
    banners: [
      {
        kind: 'info',
        message: 'Indexing…',
        testId: 'mention-banner-indexing',
      },
    ],
    sections: [
      {
        id: 'browser',
        label: 'From browser',
        items: browseItems,
      },
    ],
  };
}

export type LegOutcomeLike = { ok: boolean; error?: string } | null;

export function searchingTriggerBanners(): TriggerBanner[] {
  return [
    {
      kind: 'info',
      message: 'Searching…',
      testId: 'mention-banner-loading',
    },
  ];
}

/**
 * Labeled dual-leg error banners for a ready-index `@` query.
 * Matches the ready-path banner rules in `useFileMentions` so Storybook can
 * force search-ok/glob-fail, glob-ok/search-fail, and both-fail without live
 * transport. Unlabeled empty success on leg failure is a defect.
 */
export function readyLegErrorBanners(
  searchLeg: LegOutcomeLike,
  globLeg: LegOutcomeLike
): TriggerBanner[] {
  if (!searchLeg) return [];
  const next: TriggerBanner[] = [];
  const searchOk = searchLeg.ok;
  const globOk = globLeg === null ? true : globLeg.ok;
  const globRan = globLeg !== null;

  if (searchOk && globRan && !globOk) {
    next.push({
      kind: 'error',
      message: `Glob error: ${globLeg!.error ?? 'failed'}`,
      testId: 'mention-banner-glob-error',
    });
  }
  if (!searchOk && globRan && globOk) {
    next.push({
      kind: 'error',
      message: `Search error: ${searchLeg.error ?? 'failed'}`,
      testId: 'mention-banner-search-error',
    });
  }
  if (!searchOk && (!globRan || !globOk)) {
    next.push({
      kind: 'error',
      message:
        !searchOk && globRan && !globOk
          ? `Search and glob failed: ${searchLeg.error ?? 'search'}; ${globLeg!.error ?? 'glob'}`
          : `Search error: ${searchLeg.error ?? 'failed'}`,
      testId: 'mention-banner-both-error',
    });
  }
  return next;
}

function basename(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || path;
}

function toTriggerItem(path: string): TriggerItem {
  return { id: path, label: basename(path), description: path };
}

function dedupeByPath(items: TriggerItem[]): TriggerItem[] {
  const seen = new Set<string>();
  const out: TriggerItem[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

function searchResultsToItems(
  results: SearchResult[],
  isDirByPath: Map<string, boolean>
): TriggerItem[] {
  const items: TriggerItem[] = [];
  for (const r of results) {
    if (r.entity_type !== 'file' && r.entity_type !== 'directory') continue;
    const path = r.file_path ?? r.title;
    if (!path) continue;
    isDirByPath.set(path, r.entity_type === 'directory');
    items.push(toTriggerItem(path));
  }
  return dedupeByPath(items);
}

interface LegOutcome {
  ok: boolean;
  items: TriggerItem[];
  error?: string;
}

/**
 * Bridges browsed entries + workspace search + path/glob to the ChatInterface
 * `@` trigger.
 */
export function useFileMentions(opts: UseFileMentionsOptions): {
  fileTrigger: TriggerConfig;
  toAttachments: (uiMentions: UiMentionRef[]) => ChatMentionRef[];
  handleNodeDragStart: (entry: FileTreeEntry, e: React.DragEvent) => void;
} {
  const { knownEntries, workspaceId, mode, workspacePath, indexingReady, executeLocalTool } = opts;

  const isLocal = mode === 'local';
  const isDirByPathRef = useRef(new Map<string, boolean>());

  useEffect(() => {
    const map = isDirByPathRef.current;
    for (const e of knownEntries) map.set(e.path, e.isDir);
  }, [knownEntries]);

  const [query, setQuery] = useState('');
  const [banners, setBanners] = useState<TriggerBanner[]>([]);
  const [sections, setSections] = useState<TriggerSection[] | undefined>(undefined);
  const [items, setItems] = useState<TriggerItem[]>([]);
  const [emptyMessage, setEmptyMessage] = useState<string | undefined>(undefined);

  const browseItems = useMemo(() => knownEntries.map((e) => toTriggerItem(e.path)), [knownEntries]);

  const runGlobLeg = useCallback(
    async (pattern: string): Promise<LegOutcome> => {
      if (isLocal) {
        if (!executeLocalTool || !workspacePath) {
          return {
            ok: false,
            items: [],
            error: 'Local glob requires a running daemon and workspace path',
          };
        }
        try {
          const res = await executeLocalTool('local_file_search', {
            pattern,
            workspaceRoot: workspacePath,
          });
          if (res.error) {
            return { ok: false, items: [], error: res.error };
          }
          const entries = res.entries;
          const matches = res.matches;
          const out: TriggerItem[] = [];
          if (Array.isArray(entries)) {
            for (const e of entries) {
              isDirByPathRef.current.set(e.name, e.is_dir === true);
              out.push(toTriggerItem(e.name));
            }
          } else if (Array.isArray(matches)) {
            for (const p of matches) {
              if (!isDirByPathRef.current.has(p)) {
                isDirByPathRef.current.set(p, false);
              }
              out.push(toTriggerItem(p));
            }
          } else {
            return {
              ok: false,
              items: [],
              error: 'local_file_search returned no matches array',
            };
          }
          return { ok: true, items: dedupeByPath(out) };
        } catch (err) {
          return {
            ok: false,
            items: [],
            error: err instanceof Error ? err.message : String(err),
          };
        }
      }

      if (!workspaceId) {
        return { ok: false, items: [], error: 'Cloud glob requires a workspace' };
      }
      try {
        const res = await getSDK().workspaces.executeFileOp(workspaceId, {
          op_type: 'search',
          path: pattern,
        });
        if (res.status === 'error' || res.error) {
          return {
            ok: false,
            items: [],
            error: res.error ?? 'sandbox search failed',
          };
        }
        const meta = res.metadata ?? {};
        const entryList = meta.entries as
          | Array<{ path?: string; name?: string; is_dir?: boolean }>
          | undefined;
        const matchList = meta.matches as string[] | undefined;
        const out: TriggerItem[] = [];
        if (Array.isArray(entryList) && entryList.length > 0) {
          for (const e of entryList) {
            const p = e.path ?? e.name;
            if (!p) continue;
            isDirByPathRef.current.set(p, e.is_dir === true);
            out.push(toTriggerItem(p));
          }
        } else if (Array.isArray(matchList)) {
          for (const p of matchList) {
            if (!isDirByPathRef.current.has(p)) {
              isDirByPathRef.current.set(p, false);
            }
            out.push(toTriggerItem(p));
          }
        } else {
          return {
            ok: false,
            items: [],
            error: 'sandbox search returned no matches array',
          };
        }
        return { ok: true, items: dedupeByPath(out) };
      } catch (err) {
        return {
          ok: false,
          items: [],
          error: err instanceof Error ? err.message : String(err),
        };
      }
    },
    [isLocal, executeLocalTool, workspacePath, workspaceId]
  );

  const runSearchLeg = useCallback(
    async (q: string): Promise<LegOutcome> => {
      if (!workspaceId) {
        return { ok: false, items: [], error: 'Search requires a workspace' };
      }
      try {
        const resp = await getSDK().search.search({
          query: q,
          scope: 'files',
          workspace_id: [workspaceId],
          limit: SEARCH_LIMIT,
        });
        return {
          ok: true,
          items: searchResultsToItems(resp.results, isDirByPathRef.current),
        };
      } catch (err) {
        return {
          ok: false,
          items: [],
          error: err instanceof Error ? err.message : String(err),
        };
      }
    },
    [workspaceId]
  );

  useEffect(() => {
    if (!workspaceId) {
      setBanners((prev) => (prev.length === 0 ? prev : EMPTY_BANNERS));
      setSections(undefined);
      setItems((prev) => (prev === browseItems ? prev : browseItems));
      setEmptyMessage(undefined);
      return;
    }

    const q = query.trim();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        if (!q) {
          if (!cancelled) {
            if (!indexingReady) {
              const unready = unreadyBrowseTriggerState(browseItems);
              setBanners(unready.banners);
              setSections(unready.sections);
              setItems([]);
            } else {
              setBanners([]);
              setSections(undefined);
              setItems(browseItems);
            }
            setEmptyMessage(undefined);
          }
          return;
        }

        // Indexing unready: mandatory labeled Indexing… + From browser.
        // Still attempt search/glob so ready-but-stale-atom races can surface
        // hits, but never claim "No matches" while unready.
        if (!indexingReady) {
          const pathLike = isPathLikeQuery(q);
          const [searchLeg, globLeg] = await Promise.all([
            runSearchLeg(q),
            pathLike ? runGlobLeg(q) : Promise.resolve<LegOutcome | null>(null),
          ]);
          if (cancelled) return;

          const unready = unreadyBrowseTriggerState(browseItems);
          const nextBanners: TriggerBanner[] = [...unready.banners];
          if (searchLeg && !searchLeg.ok && searchLeg.error) {
            nextBanners.push({
              kind: 'error',
              message: `Search error: ${searchLeg.error}`,
              testId: 'mention-banner-search-error',
            });
          }
          if (globLeg && !globLeg.ok && globLeg.error) {
            nextBanners.push({
              kind: 'error',
              message: `Glob error: ${globLeg.error}`,
              testId: 'mention-banner-glob-error',
            });
          }

          const merged = dedupeByPath([
            ...(searchLeg?.ok ? searchLeg.items : []),
            ...(globLeg?.ok ? globLeg.items : []),
          ]);

          setBanners(nextBanners);
          setSections([
            ...unready.sections,
            ...(merged.length > 0 ? [{ id: 'search', label: 'Matches', items: merged }] : []),
          ]);
          setItems([]);
          setEmptyMessage(undefined);
          return;
        }

        // Ready: show labeled Searching… while legs are in flight, then
        // labeled failures / results.
        if (!cancelled) {
          setBanners(searchingTriggerBanners());
          setEmptyMessage(undefined);
        }
        const pathLike = isPathLikeQuery(q);
        const [searchLeg, globLeg] = await Promise.all([
          runSearchLeg(q),
          pathLike ? runGlobLeg(q) : Promise.resolve<LegOutcome | null>(null),
        ]);
        if (cancelled) return;

        // Labeled leg failures — shared with Storybook fixtures.
        const nextBanners = readyLegErrorBanners(searchLeg, globLeg);
        const searchOk = searchLeg.ok;

        const merged = dedupeByPath([
          ...(searchOk ? searchLeg.items : []),
          ...(globLeg?.ok ? globLeg.items : []),
        ]);

        setBanners(nextBanners);
        setSections(undefined);
        setItems(merged);
        if (searchOk && merged.length === 0) {
          setEmptyMessage('No matches');
        } else {
          setEmptyMessage(undefined);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, workspaceId, indexingReady, browseItems, runSearchLeg, runGlobLeg]);

  const onQueryChange = useCallback((q: string) => {
    setQuery(q);
  }, []);

  const fileTrigger = useMemo<TriggerConfig>(
    () => ({
      char: '@',
      label: 'Files',
      items,
      clientFilter: false,
      banners,
      sections,
      emptyMessage,
      onQueryChange,
    }),
    [items, banners, sections, emptyMessage, onQueryChange]
  );

  const toAttachments = useCallback((uiMentions: UiMentionRef[]): ChatMentionRef[] => {
    const attachments: ChatMentionRef[] = [];
    for (const m of uiMentions) {
      const isDir = isDirByPathRef.current.get(m.itemId);
      if (isDir === undefined) {
        throw new Error(
          `Cannot classify @-mention path "${m.itemId}" as file or directory — ` +
            `it was not returned by browse, search, or glob`
        );
      }
      attachments.push({
        type: isDir ? 'directory' : 'file',
        path: m.itemId,
        label: m.label,
      });
    }
    return attachments;
  }, []);

  const handleNodeDragStart = useCallback((entry: FileTreeEntry, e: React.DragEvent) => {
    const payload: UiMentionRef = { triggerId: '@', itemId: entry.path, label: entry.name };
    e.dataTransfer.setData(MENTION_DND_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  return { fileTrigger, toAttachments, handleNodeDragStart };
}
